// /app/frontend/src/components/compliance/map/FilterPanel.jsx
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { 
  Filter, RotateCcw, Database, Cpu, HardDrive, 
  Globe, Users, Building2, ShieldCheck, AlertCircle, AlertTriangle
} from 'lucide-react';
import { cn } from '../../../lib/utils';

const NODE_TYPES = [
  { value: 'subjects', label: 'Data Subjects', icon: Users, color: 'text-emerald-400' },
  { value: 'source', label: 'Data Sources', icon: Database, color: 'text-blue-400' },
  { value: 'process', label: 'Processing', icon: Cpu, color: 'text-purple-400' },
  { value: 'store', label: 'Storage', icon: HardDrive, color: 'text-orange-400' },
  { value: 'thirdparty', label: 'Third Parties', icon: Building2, color: 'text-cyan-400' },
  { value: 'transfer', label: 'Transfers', icon: Globe, color: 'text-rose-400' },
];

const RISK_LEVELS = [
  { value: 'low', label: 'Low Risk', icon: ShieldCheck, color: 'text-emerald-400 bg-emerald-500/10' },
  { value: 'medium', label: 'Medium Risk', icon: AlertCircle, color: 'text-amber-400 bg-amber-500/10' },
  { value: 'high', label: 'High Risk', icon: AlertTriangle, color: 'text-rose-400 bg-rose-500/10' },
];

const DATA_CATEGORIES = [
  'Name', 'Email', 'Phone', 'Address', 'Financial Data', 'Health Data',
  'Location Data', 'Biometric Data', 'Usage Data', 'Device ID'
];

const FilterPanel = ({ 
  open, 
  onOpenChange, 
  filters, 
  onFiltersChange, 
  entities = [],
  onReset 
}) => {
  const toggleNodeType = (type) => {
    const current = filters.nodeTypes || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    onFiltersChange({ ...filters, nodeTypes: updated });
  };

  const toggleRiskLevel = (risk) => {
    const current = filters.riskLevels || [];
    const updated = current.includes(risk)
      ? current.filter(r => r !== risk)
      : [...current, risk];
    onFiltersChange({ ...filters, riskLevels: updated });
  };

  const toggleDataCategory = (category) => {
    const current = filters.dataCategories || [];
    const updated = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    onFiltersChange({ ...filters, dataCategories: updated });
  };

  const activeFilterCount = 
    (filters.nodeTypes?.length || 0) + 
    (filters.riskLevels?.length || 0) + 
    (filters.dataCategories?.length || 0) +
    (filters.entityId ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-slate-950 border-slate-800 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Filter className="h-5 w-5 text-violet-400" />
            Filter Nodes
            {activeFilterCount > 0 && (
              <Badge variant="outline" className="ml-2 bg-violet-500/10 text-violet-400">
                {activeFilterCount} active
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Filter the data flow map to focus on specific nodes
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-6">
          {/* Entity Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-300">Entity</Label>
            <Select 
              value={filters.entityId || 'all'} 
              onValueChange={(v) => onFiltersChange({ ...filters, entityId: v === 'all' ? '' : v })}
            >
              <SelectTrigger className="bg-slate-900 border-slate-800">
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entities.map((entity) => (
                  <SelectItem key={entity.id} value={entity.id}>
                    {entity.entity_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Node Types */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-300">Node Types</Label>
            <div className="space-y-2">
              {NODE_TYPES.map(({ value, label, icon: Icon, color }) => (
                <div
                  key={value}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all",
                    (filters.nodeTypes || []).includes(value)
                      ? "bg-slate-800"
                      : "hover:bg-slate-900"
                  )}
                  onClick={() => toggleNodeType(value)}
                >
                  <Checkbox
                    checked={(filters.nodeTypes || []).includes(value)}
                    onCheckedChange={() => toggleNodeType(value)}
                  />
                  <Icon className={cn("h-4 w-4", color)} />
                  <span className="text-sm text-slate-300">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Levels */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-300">Risk Level</Label>
            <div className="space-y-2">
              {RISK_LEVELS.map(({ value, label, icon: Icon, color }) => (
                <div
                  key={value}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all",
                    (filters.riskLevels || []).includes(value)
                      ? "bg-slate-800"
                      : "hover:bg-slate-900"
                  )}
                  onClick={() => toggleRiskLevel(value)}
                >
                  <Checkbox
                    checked={(filters.riskLevels || []).includes(value)}
                    onCheckedChange={() => toggleRiskLevel(value)}
                  />
                  <Icon className={cn("h-4 w-4", color.split(' ')[0])} />
                  <span className="text-sm text-slate-300">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Data Categories */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-300">Data Categories</Label>
            <div className="flex flex-wrap gap-2">
              {DATA_CATEGORIES.map((category) => (
                <Badge
                  key={category}
                  variant="outline"
                  className={cn(
                    "cursor-pointer transition-all",
                    (filters.dataCategories || []).includes(category)
                      ? "bg-violet-500/20 text-violet-300 border-violet-500/50"
                      : "bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-600"
                  )}
                  onClick={() => toggleDataCategory(category)}
                >
                  {category}
                </Badge>
              ))}
            </div>
          </div>

          {/* Reset Button */}
          <Button
            variant="outline"
            className="w-full"
            onClick={onReset}
            disabled={activeFilterCount === 0}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Filters
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilterPanel;
